import { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Table,
  TextInput,
  Button,
  Paper,
  Group,
  Text,
  Badge,
  Loader,
  Stack,
  Divider,
  Pagination,
  ActionIcon,
  Tooltip,
  Tabs,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconRefresh,
  IconLock,
  IconFilter,
  IconAlertTriangle,
} from "@tabler/icons-react";
import {
  useGrossingQueue,
  useOpenCaseByBarcode,
} from "../../hooks/useWorkflows";
import {
  CaseStatusBadge,
  FilterModal,
  ActiveFilters,
} from "../../components/cases";
import { useAuthStore } from "../../store/auth.store";
import { type CaseFilters } from "../../services";
import {
  isCompleteGrossingBarcode,
  useBarcodeScanner,
} from "../../hooks/useBarcodeScanner";
import { formatDateOnlyForDisplay, parseDateOnly } from "../../utils/dateOnly";

const ITEMS_PER_PAGE = 20;

function formatQueueDate(value: unknown): string {
  const dateOnly = parseDateOnly(value);
  return dateOnly ? formatDateOnlyForDisplay(dateOnly) : "—";
}

export default function GrossingQueuePage() {
  const navigate = useNavigate();
  const [barcodeInput, setBarcodeInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<CaseFilters>({});
  const [page, setPage] = useState(1);
  const [blockedPage, setBlockedPage] = useState(1);
  const [embeddingPage, setEmbeddingPage] = useState(1);
  const [activeTab, setActiveTab] = useState<string>("queue");
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const currentUserId = useAuthStore((state) => state.profile?.id);

  const {
    data: queueCases,
    isLoading,
    isFetching,
    refetch,
  } = useGrossingQueue(filters);

  // "In Embedding" tab query — uses same filters but different statuses
  const embeddingFilters = useMemo(
    () => ({
      ...filters,
      statusNames: ["Pending Tissue Processing", "Embedding"],
    }),
    [filters]
  );
  const { data: embeddingCases, isLoading: embeddingLoading } =
    useGrossingQueue(embeddingFilters);

  const openCaseMutation = useOpenCaseByBarcode();

  // Split cases into active and blocked
  const { activeCases, blockedCases } = useMemo(() => {
    if (!queueCases) return { activeCases: [], blockedCases: [] };
    return {
      activeCases: queueCases.filter((c: any) => !c.isBlocked),
      blockedCases: queueCases.filter((c: any) => c.isBlocked),
    };
  }, [queueCases]);

  // Pagination calculations
  const totalActivePages = Math.ceil(activeCases.length / ITEMS_PER_PAGE);
  const totalBlockedPages = Math.ceil(blockedCases.length / ITEMS_PER_PAGE);

  const paginatedActiveCases = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return activeCases.slice(start, start + ITEMS_PER_PAGE);
  }, [activeCases, page]);

  const paginatedBlockedCases = useMemo(() => {
    const start = (blockedPage - 1) * ITEMS_PER_PAGE;
    return blockedCases.slice(start, start + ITEMS_PER_PAGE);
  }, [blockedCases, blockedPage]);

  // "In Embedding" tab pagination
  const embeddingCasesList = useMemo(
    () => embeddingCases || [],
    [embeddingCases]
  );
  const totalEmbeddingPages = Math.ceil(
    embeddingCasesList.length / ITEMS_PER_PAGE
  );
  const paginatedEmbeddingCases = useMemo(() => {
    const start = (embeddingPage - 1) * ITEMS_PER_PAGE;
    return embeddingCasesList.slice(start, start + ITEMS_PER_PAGE);
  }, [embeddingCasesList, embeddingPage]);

  // Reset to page 1 when filter results change (prevents showing empty pages).
  // `page` / `blockedPage` intentionally omitted — including them would cause infinite reset loops.
  useEffect(() => {
    if (page > totalActivePages && totalActivePages > 0) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCases.length, totalActivePages]);

  useEffect(() => {
    if (blockedPage > totalBlockedPages && totalBlockedPages > 0) {
      setBlockedPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockedCases.length, totalBlockedPages]);

  // Check if any filters are active (excluding search)
  const hasActiveFilters = Object.values(filters).some((value) => {
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    if (typeof value === "boolean") return value;
    return !!value;
  });

  const handleFiltersApply = (newFilters: CaseFilters) => {
    setFilters(newFilters);
    setPage(1);
    setBlockedPage(1);
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPage(1);
    setBlockedPage(1);
  };

  const handleBarcodeSubmit = async () => {
    if (!barcodeInput.trim()) {
      return;
    }

    setError(null);

    const trimmed = barcodeInput.trim();
    const parts = trimmed.split(";");

    // Validate barcode format: must have at least CASE;SPECIMEN
    if (parts.length < 2 || !parts[1]?.trim()) {
      setError(
        "Invalid barcode format. Expected: CASE;SPECIMEN (e.g., G25-00001;A)"
      );
      return;
    }

    const specimenLetter = parts[1].trim();

    try {
      const caseData = await openCaseMutation.mutateAsync({
        barcode: trimmed,
        intent: "grossing",
      });
      navigate(`/grossing/${caseData.id}`, {
        state: { specimenLetter },
      });
    } catch (err: any) {
      setError(err.message || "Failed to open case");
      setBarcodeInput("");
    }
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleBarcodeSubmit();
    }
  };

  useBarcodeScanner(
    useCallback(
      async (barcode: string) => {
        setBarcodeInput(barcode);
        setError(null);

        const parts = barcode.split(";");
        if (parts.length < 2 || !parts[1]?.trim()) {
          setError(
            "Invalid barcode format. Expected: CASE;SPECIMEN (e.g., G25-00001;A)"
          );
          return;
        }

        const specimenLetter = parts[1].trim();

        try {
          const caseData = await openCaseMutation.mutateAsync({
            barcode,
            intent: "grossing",
          });
          navigate(`/grossing/${caseData.id}`, {
            state: { specimenLetter },
          });
        } catch (err: any) {
          setError(err.message || "Failed to open case");
          setBarcodeInput("");
        }
      },
      [openCaseMutation, navigate]
    ),
    {
      enabled: !openCaseMutation.isPending,
      ownerKey: "grossing-queue",
      isCompleteBarcode: isCompleteGrossingBarcode,
    }
  );

  const handleRowClick = (caseId: string, lockedByOther: boolean) => {
    if (lockedByOther) {
      navigate(`/cases/${caseId}`);
    } else {
      navigate(`/grossing/${caseId}`);
    }
  };

  const renderTableHeader = () => (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>Case</Table.Th>
        <Table.Th>Patient</Table.Th>
        <Table.Th>Procedure</Table.Th>
        <Table.Th>Status</Table.Th>
        <Table.Th>Priority</Table.Th>
        <Table.Th>Workflow</Table.Th>
        <Table.Th>Pathologist</Table.Th>
        <Table.Th>Facility</Table.Th>
        <Table.Th>Specimens</Table.Th>
        <Table.Th>Action</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  const renderCaseRow = (caseItem: any, isBlockedTable = false) => {
    const isLockedByOther =
      caseItem.lockedBy && caseItem.lockedBy !== currentUserId;
    const isInProgress = caseItem.currentStatus?.name === "Grossing";

    return (
      <Table.Tr
        key={caseItem.id}
        style={{
          cursor: isBlockedTable ? "not-allowed" : "pointer",
          opacity: isLockedByOther ? 0.6 : 1,
        }}
        onClick={() =>
          !isBlockedTable && handleRowClick(caseItem.id, isLockedByOther)
        }
      >
        {/* Case: Case # + External # + Date */}
        <Table.Td>
          <Stack gap={2}>
            <Text size="sm" fw={500}>
              {caseItem.caseNumber}
            </Text>
            {caseItem.externalCaseNumber && (
              <Badge size="xs" variant="outline" color="gray">
                Ext: {caseItem.externalCaseNumber}
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              {formatQueueDate(caseItem.dateReceived)}
            </Text>
          </Stack>
        </Table.Td>
        {/* Patient: Name + DOB + MRN */}
        <Table.Td>
          {caseItem.patient ? (
            <Stack gap={0}>
              <Text size="sm">
                {caseItem.patient.lastName}, {caseItem.patient.firstName}
              </Text>
              {caseItem.patient.dateOfBirth && (
                <Text size="xs" c="dimmed">
                  DOB: {formatQueueDate(caseItem.patient.dateOfBirth)}
                </Text>
              )}
              {caseItem.patient.medicalRecordNumber && (
                <Text size="xs" c="dimmed">
                  MRN: {caseItem.patient.medicalRecordNumber}
                </Text>
              )}
            </Stack>
          ) : (
            <Text size="sm" c="dimmed">
              —
            </Text>
          )}
        </Table.Td>
        {/* Procedure */}
        <Table.Td>
          <Text size="sm">{caseItem.procedure?.name || "—"}</Text>
        </Table.Td>
        {/* Status: Lock + Status badge */}
        <Table.Td>
          <Group gap={4} wrap="nowrap">
            {caseItem.lockedBy && (
              <Tooltip
                label={`Locked by ${caseItem.lockedByName ?? "another user"}`}
                withArrow
              >
                <IconLock
                  size={14}
                  color={isLockedByOther ? "orange" : "green"}
                />
              </Tooltip>
            )}
            <CaseStatusBadge
              statusName={caseItem.currentStatus?.name || "Unknown"}
              isBlocked={caseItem.isBlocked}
            />
          </Group>
        </Table.Td>
        {/* Priority: Rush/High Priority/Normal */}
        <Table.Td>
          {caseItem.isRush || caseItem.isHighPriority ? (
            <Group gap={4} wrap="nowrap">
              {caseItem.isRush && (
                <Tooltip
                  label={
                    caseItem.rushReason || "Rush case — expedited processing"
                  }
                  multiline
                  maw={300}
                >
                  <Badge color="red" variant="filled" size="xs">
                    RUSH
                  </Badge>
                </Tooltip>
              )}
              {caseItem.isHighPriority && (
                <Tooltip
                  label={
                    caseItem.highPriorityReason ||
                    "High priority — flagged for attention"
                  }
                  multiline
                  maw={300}
                >
                  <Badge color="orange" variant="filled" size="xs">
                    HIGH
                  </Badge>
                </Tooltip>
              )}
            </Group>
          ) : (
            <Text size="sm" c="dimmed">
              Normal
            </Text>
          )}
        </Table.Td>
        {/* Workflow */}
        <Table.Td>
          <Text size="sm">
            {caseItem.workflow?.name ||
              (caseItem.workflow?.accessioningType
                ? caseItem.workflow.accessioningType
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (c: string) => c.toUpperCase())
                : "—")}
          </Text>
        </Table.Td>
        {/* Pathologist */}
        <Table.Td>
          <Text size="sm">
            {caseItem.pathologist?.fullName || "Unassigned"}
          </Text>
        </Table.Td>
        {/* Facility */}
        <Table.Td>
          <Text size="sm">{caseItem.facility?.name || "—"}</Text>
        </Table.Td>
        {/* Specimens & Blocks */}
        <Table.Td>
          <Stack gap={0}>
            <Text size="sm">{caseItem.specimenCount || 0} specimens</Text>
            <Text size="sm" c="dimmed">
              {caseItem.blockCount || 0} blocks
            </Text>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Button
            size="xs"
            variant={isBlockedTable || isLockedByOther ? "subtle" : "light"}
            onClick={(e) => {
              e.stopPropagation();
              navigate(
                isBlockedTable || isLockedByOther
                  ? `/cases/${caseItem.id}${
                      isBlockedTable ? "?tab=errors" : ""
                    }`
                  : `/grossing/${caseItem.id}`
              );
            }}
          >
            {isBlockedTable || isLockedByOther
              ? "View"
              : isInProgress
              ? "Continue"
              : "Start"}
          </Button>
        </Table.Td>
      </Table.Tr>
    );
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          <Title order={2}>Grossing Queue</Title>

          {/* Barcode Scanner and Filters Row */}
          <Group align="stretch" grow>
            {/* Barcode Scanner Input */}
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconBarcode size={24} />
                  <Text fw={500}>Scan Cassette Barcode</Text>
                </Group>
                <Group>
                  <TextInput
                    placeholder="Scan or enter barcode (e.g., G25-00001;A;1)"
                    value={barcodeInput}
                    onChange={(e) => setBarcodeInput(e.target.value)}
                    onKeyDown={handleBarcodeKeyDown}
                    style={{ flex: 1 }}
                    autoFocus
                    error={error}
                  />
                  <Button
                    onClick={handleBarcodeSubmit}
                    loading={openCaseMutation.isPending}
                    disabled={!barcodeInput.trim()}
                  >
                    Open Case
                  </Button>
                </Group>
                <Text size="sm" c="dimmed">
                  Scan a cassette barcode to quickly open the case for grossing
                </Text>
              </Stack>
            </Paper>

            {/* Queue Filters */}
            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={24} />
                    <Text fw={500}>Queue Filters</Text>
                  </Group>
                  <ActionIcon
                    variant="subtle"
                    size="lg"
                    onClick={() => refetch()}
                    loading={isFetching}
                  >
                    <IconRefresh size={18} />
                  </ActionIcon>
                </Group>
                <Button variant="light" onClick={openFilterModal} fullWidth>
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <ActiveFilters
                    filters={filters}
                    onFiltersChange={setFilters}
                    onClearAll={handleClearAllFilters}
                  />
                )}
                <Text size="sm" c="dimmed">
                  Showing {queueCases?.length || 0} cases
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(value) => {
              if (value) {
                setActiveTab(value);
                setPage(1);
                setBlockedPage(1);
                setEmbeddingPage(1);
              }
            }}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="queue">
                Queue ({queueCases?.length || 0})
              </Tabs.Tab>
              <Tabs.Tab
                value="embedding"
                rightSection={
                  embeddingCasesList.length > 0 ? (
                    <Badge size="sm" variant="filled" color="blue">
                      {embeddingCasesList.length}
                    </Badge>
                  ) : undefined
                }
              >
                In Embedding
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="queue" pt="md">
              <Stack gap="lg">
                {/* Active Cases Table */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    <Group justify="space-between">
                      <Text fw={500} size="lg">
                        Active Queue ({activeCases.length})
                      </Text>
                      {totalActivePages > 1 && (
                        <Group gap="md">
                          <Text size="sm" c="dimmed">
                            Showing {(page - 1) * ITEMS_PER_PAGE + 1}-
                            {Math.min(
                              page * ITEMS_PER_PAGE,
                              activeCases.length
                            )}{" "}
                            of {activeCases.length} cases
                          </Text>
                          <Pagination
                            total={totalActivePages}
                            value={page}
                            onChange={setPage}
                            size="sm"
                          />
                        </Group>
                      )}
                    </Group>

                    {isLoading ? (
                      <Group justify="center" p="xl">
                        <Loader />
                      </Group>
                    ) : activeCases.length === 0 ? (
                      <Text c="dimmed" ta="center" py="xl">
                        {hasActiveFilters
                          ? "No cases match the current filters"
                          : "No cases pending grossing"}
                      </Text>
                    ) : (
                      <Table striped highlightOnHover>
                        {renderTableHeader()}
                        <Table.Tbody>
                          {paginatedActiveCases.map((caseItem: any) =>
                            renderCaseRow(caseItem)
                          )}
                        </Table.Tbody>
                      </Table>
                    )}
                  </Stack>
                </Paper>

                {/* Blocked Cases Table */}
                {blockedCases.length > 0 && (
                  <Paper p="md" withBorder>
                    <Stack gap="md">
                      <Group justify="space-between">
                        <Group gap="sm">
                          <IconAlertTriangle
                            size={20}
                            color="var(--mantine-color-red-6)"
                          />
                          <Text fw={500} size="lg" c="red">
                            Blocked Cases ({blockedCases.length})
                          </Text>
                        </Group>
                        {totalBlockedPages > 1 && (
                          <Group gap="md">
                            <Text size="sm" c="dimmed">
                              Showing {(blockedPage - 1) * ITEMS_PER_PAGE + 1}-
                              {Math.min(
                                blockedPage * ITEMS_PER_PAGE,
                                blockedCases.length
                              )}{" "}
                              of {blockedCases.length} cases
                            </Text>
                            <Pagination
                              total={totalBlockedPages}
                              value={blockedPage}
                              onChange={setBlockedPage}
                              size="sm"
                            />
                          </Group>
                        )}
                      </Group>
                      <Text size="sm" c="dimmed">
                        These cases have reported errors and are blocked from
                        proceeding until the issue is resolved.
                      </Text>
                      <Divider />
                      <Table striped>
                        {renderTableHeader()}
                        <Table.Tbody>
                          {paginatedBlockedCases.map((caseItem: any) =>
                            renderCaseRow(caseItem, true)
                          )}
                        </Table.Tbody>
                      </Table>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="embedding" pt="md">
              <Paper p="md" withBorder>
                <Stack gap="md">
                  <Group justify="space-between">
                    <div>
                      <Text fw={500} size="lg">
                        In Embedding ({embeddingCasesList.length})
                      </Text>
                      <Text size="sm" c="dimmed">
                        Cases in tissue processing and embedding — gross
                        descriptions still editable
                      </Text>
                    </div>
                    {totalEmbeddingPages > 1 && (
                      <Group gap="md">
                        <Text size="sm" c="dimmed">
                          Showing {(embeddingPage - 1) * ITEMS_PER_PAGE + 1}-
                          {Math.min(
                            embeddingPage * ITEMS_PER_PAGE,
                            embeddingCasesList.length
                          )}{" "}
                          of {embeddingCasesList.length} cases
                        </Text>
                        <Pagination
                          total={totalEmbeddingPages}
                          value={embeddingPage}
                          onChange={setEmbeddingPage}
                          size="sm"
                        />
                      </Group>
                    )}
                  </Group>

                  {embeddingLoading ? (
                    <Group justify="center" p="xl">
                      <Loader />
                    </Group>
                  ) : embeddingCasesList.length === 0 ? (
                    <Text c="dimmed" ta="center" py="xl">
                      No cases currently in embedding
                    </Text>
                  ) : (
                    <Table striped highlightOnHover>
                      {renderTableHeader()}
                      <Table.Tbody>
                        {paginatedEmbeddingCases.map((caseItem: any) => (
                          <Table.Tr
                            key={caseItem.id}
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/grossing/${caseItem.id}`)}
                          >
                            <Table.Td>
                              <Stack gap={2}>
                                <Text size="sm" fw={500}>
                                  {caseItem.caseNumber}
                                </Text>
                                {caseItem.externalCaseNumber && (
                                  <Badge
                                    size="xs"
                                    variant="outline"
                                    color="gray"
                                  >
                                    Ext: {caseItem.externalCaseNumber}
                                  </Badge>
                                )}
                                <Text size="xs" c="dimmed">
                                  {formatQueueDate(caseItem.dateReceived)}
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              {caseItem.patient ? (
                                <Stack gap={0}>
                                  <Text size="sm">
                                    {caseItem.patient.lastName},{" "}
                                    {caseItem.patient.firstName}
                                  </Text>
                                  {caseItem.patient.dateOfBirth && (
                                    <Text size="xs" c="dimmed">
                                      DOB:{" "}
                                      {formatQueueDate(
                                        caseItem.patient.dateOfBirth
                                      )}
                                    </Text>
                                  )}
                                </Stack>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  —
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {caseItem.procedure?.name || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <CaseStatusBadge
                                statusName={
                                  caseItem.currentStatus?.name || "Unknown"
                                }
                                isBlocked={caseItem.isBlocked}
                              />
                            </Table.Td>
                            <Table.Td>
                              {caseItem.isRush ? (
                                <Badge color="red" variant="filled" size="xs">
                                  RUSH
                                </Badge>
                              ) : caseItem.isHighPriority ? (
                                <Badge
                                  color="orange"
                                  variant="filled"
                                  size="xs"
                                >
                                  HIGH
                                </Badge>
                              ) : (
                                <Text size="sm" c="dimmed">
                                  Normal
                                </Text>
                              )}
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {caseItem.workflow?.name || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {caseItem.pathologist?.fullName || "Unassigned"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">
                                {caseItem.facility?.name || "—"}
                              </Text>
                            </Table.Td>
                            <Table.Td>
                              <Stack gap={0}>
                                <Text size="sm">
                                  {caseItem.specimenCount || 0} specimens
                                </Text>
                                <Text size="sm" c="dimmed">
                                  {caseItem.blockCount || 0} blocks
                                </Text>
                              </Stack>
                            </Table.Td>
                            <Table.Td>
                              <Button
                                size="xs"
                                variant="light"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/grossing/${caseItem.id}`);
                                }}
                              >
                                Open
                              </Button>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  )}
                </Stack>
              </Paper>
            </Tabs.Panel>
          </Tabs>
        </Stack>

        {/* Filter Modal */}
        <FilterModal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          filters={filters}
          onApply={handleFiltersApply}
          allowedStatusNames={
            activeTab === "embedding"
              ? ["Pending Tissue Processing", "Embedding"]
              : ["Pending Grossing", "Grossing"]
          }
        />
      </Box>
    </Container>
  );
}
